import { Controller } from "@hotwired/stimulus"

// Opens blog images in an overlay instead of a new browser tab. Attach to a
// container (e.g. the blog <article>); each image link is an `image` target and
// carries `data-action="click->lightbox#open"`, keeping its href as a no-JS
// fallback. Builds a native <dialog> on demand so Escape and the backdrop close
// for free, steps through the gallery (arrows / chevrons / swipe), and overlays
// a paired caption (data-caption) plus a position counter.
export default class extends Controller {
  static targets = ["image"]

  open(event) {
    const link = event.currentTarget
    if (!link.getAttribute("href")) return

    event.preventDefault()
    this.index = this.imageTargets.indexOf(link)
    this.show()
  }

  show() {
    const dialog = document.createElement("dialog")
    dialog.className =
      "lightbox bg-transparent p-0 m-auto max-w-none max-h-none backdrop:bg-black/80"

    this.img = document.createElement("img")
    this.img.alt = ""
    this.img.className =
      "block max-w-[92vw] max-h-[88vh] w-auto h-auto rounded-md shadow-2xl"

    // Spinner shown while an image loads (only really visible on first open;
    // neighbours are preloaded so stepping is instant).
    this.spinner = document.createElement("div")
    this.spinner.className = "absolute inset-0 grid place-items-center pointer-events-none"
    this.spinner.innerHTML =
      '<span class="w-10 h-10 border-4 border-white/30 border-t-white ' +
      'rounded-full animate-spin"></span>'

    // Caption overlaid on the image, fed per-image from each link's
    // data-caption (set when a text row is paired with the image row).
    this.caption = document.createElement("div")
    this.caption.className =
      "absolute inset-x-0 bottom-0 p-5 sm:p-6 pt-10 rounded-b-md " +
      "bg-gradient-to-t from-black/80 via-black/45 to-transparent " +
      "text-white text-lg sm:text-xl leading-snug [&_*]:text-white [&_a]:underline [&_p]:m-0 space-y-1"

    const frame = document.createElement("div")
    frame.className = "relative"
    frame.appendChild(this.img)
    frame.appendChild(this.spinner)
    frame.appendChild(this.caption)
    frame.appendChild(this.button("Sluiten", "M6 6l12 12M18 6L6 18", "top-2 right-2", () => dialog.close()))

    // Prev/next + counter only make sense with more than one image.
    if (this.imageTargets.length > 1) {
      this.counter = document.createElement("div")
      this.counter.className =
        "absolute top-2 left-2 px-2.5 py-1 rounded-full bg-black/50 text-white text-xs tabular-nums"
      frame.appendChild(this.counter)
      frame.appendChild(this.button("Vorige", "M15 6l-6 6 6 6", "top-1/2 -translate-y-1/2 left-2", () => this.step(-1)))
      frame.appendChild(this.button("Volgende", "M9 6l6 6-6 6", "top-1/2 -translate-y-1/2 right-2", () => this.step(1)))
    }

    dialog.appendChild(frame)
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close()
    })
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.step(-1)
      else if (event.key === "ArrowRight") this.step(1)
    })
    dialog.addEventListener("touchstart", (event) => {
      this.touchX = event.changedTouches[0].clientX
    }, { passive: true })
    dialog.addEventListener("touchend", (event) => {
      if (this.touchX == null) return
      const dx = event.changedTouches[0].clientX - this.touchX
      this.touchX = null
      if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1)
    })
    dialog.addEventListener("close", () => {
      dialog.remove()
      this.dialog = this.img = this.caption = this.counter = this.spinner = null
    })

    this.dialog = dialog
    document.body.appendChild(dialog)
    dialog.showModal()
    this.render()
  }

  step(delta) {
    const count = this.imageTargets.length
    this.index = (this.index + delta + count) % count
    this.render()
  }

  render() {
    const link = this.imageTargets[this.index]

    if (this.img) {
      this.spinner.hidden = false
      this.img.onload = this.img.onerror = () => { if (this.spinner) this.spinner.hidden = true }
      this.img.src = this.fullSrc(link)
    }
    if (this.caption) {
      const html = link.dataset.caption || ""
      this.caption.innerHTML = html
      this.caption.hidden = html === ""
    }
    if (this.counter) {
      this.counter.textContent = `${this.index + 1} / ${this.imageTargets.length}`
    }
    this.preloadNeighbours()
  }

  // Warm the browser cache for the adjacent images so stepping feels instant.
  preloadNeighbours() {
    const count = this.imageTargets.length
    if (count < 2) return
    for (const offset of [1, -1]) {
      const neighbour = this.imageTargets[(this.index + offset + count) % count]
      const preload = new Image()
      preload.src = this.fullSrc(neighbour)
    }
  }

  // The lightbox prefers a sized variant (data-full); href is the original and
  // doubles as the no-JS fallback.
  fullSrc(link) {
    return link.dataset.full || link.getAttribute("href")
  }

  // Builds an overlay control: a round button positioned by `place`, with an
  // SVG `path`, an accessible label, and a click handler.
  button(label, path, place, onClick) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.setAttribute("aria-label", label)
    btn.className =
      `absolute ${place} grid place-items-center w-9 h-9 rounded-full ` +
      "bg-white/90 text-brand-deep shadow-card hover:bg-white transition"
    btn.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" ' +
      `stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="${path}"/></svg>`
    btn.addEventListener("click", onClick)
    return btn
  }
}
